import { formatInTimeZone } from 'date-fns-tz';

import { translation, formatToReactComponent, TranslatedComponent } from '@hh.ru/front-static-app';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';
import { SPALink, usePush } from '@hh.ru/redux-spa-middleware';
import Button, { ButtonKind, ButtonAppearance } from 'bloko/blocks/button';
import Column, { ColumnsRow } from 'bloko/blocks/column';
import { ClockScaleMediumKindDefault, IconColor } from 'bloko/blocks/icon';
import BlokoLink from 'bloko/blocks/link';
import Loading from 'bloko/blocks/loading';
import Text, { TextImportance, TextSize } from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';

import { COMPLETE_RESUME_PACKAGES } from 'src/types/applicantServices';

import styles from './index.less';

const MOSCOW_TIMEZONE = 'Europe/Moscow';

const TrlKeys = {
    awaitingText: 'applicantServices.awaitingForCall.awaitingText',
    datetime: 'applicantServices.awaitingForCall.datetime',
    meetingUrl: 'applicantServices.awaitingForCall.meetingUrl',
    meetingLink: 'applicantServices.awaitingForCall.meetingUrl.link',
    email: 'applicantServices.awaitingForCall.email',
    phone: 'applicantServices.awaitingForCall.phone',
    resumeAbout: 'applicantServices.awaitingForCall.resumeAbout',
    consultationAbout: 'applicantServices.awaitingForCall.consultationAbout',
    datetimeConsult: 'applicantServices.awaitingForCall.datetime.text',
    actionChange: 'applicantServices.awaitingForCall.actionChange',
    duringTime: {
        [COMPLETE_RESUME_PACKAGES.BASE]: 'applicantServices.awaitingForCall.duringTime.base',
        [COMPLETE_RESUME_PACKAGES.OPTIMAL]: 'applicantServices.awaitingForCall.duringTime.optimal',
        [COMPLETE_RESUME_PACKAGES.MAXIMAL]: 'applicantServices.awaitingForCall.duringTime.maximal',
        [COMPLETE_RESUME_PACKAGES.CONSULT_ONLY]: 'applicantServices.awaitingForCall.duringTime.consultOnly',
        [COMPLETE_RESUME_PACKAGES.INTERVIEW_PRACTICE]: 'applicantServices.awaitingForCall.duringTime.interviewPractice',
    },
    duringCallTime: 'applicantServices.awaitingForCall.duringTime.callTime',
    prepareTitle1: 'applicantServices.awaitingForCall.prepareTitle.theoretical',
    prepareTitle2: 'applicantServices.awaitingForCall.prepareTitle.practical',
    prepareDescription1: 'applicantServices.awaitingForCall.prepareDescription.theoretical',
    prepareDescription2: 'applicantServices.awaitingForCall.prepareDescription.practical',
    prepareButton: 'applicantServices.awaitingForCall.prepareButton',
};

const Awaiting: TranslatedComponent = ({ trls }) => {
    const order = useSelectorNonNullable(({ careerServiceOrder }) => careerServiceOrder);
    const resumes = useSelectorNonNullable((state) => state.resumeIdentifiers);
    const pathname = useSelectorNonNullable((state) => state.router.location.pathname);
    const selectedResume = resumes.filter((resume) => resume.hash === order.resumeHash)[0];
    const packageType = order.packageType;
    const duringTime = trls[TrlKeys.duringTime[packageType]];
    const push = usePush();

    return (
        <Column xs="4" s="6" m="9" l="11" container>
            {packageType === COMPLETE_RESUME_PACKAGES.INTERVIEW_PRACTICE && (
                <>
                    <ColumnsRow>
                        <Column xs="4" s="6" m="8" l="8">
                            <Text size={TextSize.Large} strong>
                                {trls[TrlKeys.prepareTitle1]}
                            </Text>
                        </Column>
                        <VSpacing base={2} />
                        <Column xs="4" s="6" m="8" l="8">
                            <Text size={TextSize.Large}>{trls[TrlKeys.prepareDescription1]}</Text>
                        </Column>
                        <VSpacing base={4} />
                        <Column xs="4" s="6" m="8" l="8">
                            <Button
                                Element={SPALink}
                                to="/interview_practice_guide"
                                target="_blank"
                                kind={ButtonKind.Primary}
                                appearance={ButtonAppearance.Outlined}
                            >
                                {trls[TrlKeys.prepareButton]}
                            </Button>
                        </Column>
                    </ColumnsRow>
                    <VSpacing base={6} />
                    <div className={styles.separator} />
                    <VSpacing base={6} />

                    <ColumnsRow>
                        <Column xs="4" s="6" m="8" l="8">
                            <Text size={TextSize.Large} strong>
                                {trls[TrlKeys.prepareTitle2]}
                            </Text>
                        </Column>
                        <VSpacing base={2} />
                        <Column xs="4" s="6" m="8" l="8">
                            <Text size={TextSize.Large}>{trls[TrlKeys.prepareDescription2]}</Text>
                        </Column>
                    </ColumnsRow>
                    <VSpacing base={6} />
                    <div className={styles.separator} />
                    <VSpacing base={10} />
                </>
            )}
            <ColumnsRow>
                <Column xs="4" s="6" m="3" l="4">
                    <Text size={TextSize.ExtraLarge}>{trls[TrlKeys.awaitingText]}</Text>
                </Column>
                <Column xs="4" s="6" m="6" l="7">
                    <div className={styles.awaitingDate}>
                        <span className={styles.awaitingDateIcon}>
                            <ClockScaleMediumKindDefault initial={IconColor.Green60} />
                        </span>
                        <Text Element="span" strong>
                            {trls[TrlKeys.datetime]}
                        </Text>
                    </div>
                    <Text strong size={TextSize.Small} importance={TextImportance.Tertiary}>
                        {order.isPlanerka
                            ? formatToReactComponent(trls[TrlKeys.duringCallTime], {
                                  '{0}': order.callDuration,
                              })
                            : duringTime}
                    </Text>
                </Column>
            </ColumnsRow>
            <VSpacing base={6} />
            <div className={styles.separator} />
            <VSpacing base={6} />
            <ColumnsRow>
                <Column xs="4" s="6" m="3" l="4">
                    <Text importance={TextImportance.Tertiary}>{trls[TrlKeys.datetime]}</Text>
                </Column>
                <Column xs="4" s="6" m="6" l="7">
                    {order.callTimeBegin && order.callTimeEnd ? (
                        <Text>
                            {formatToReactComponent(trls[TrlKeys.datetimeConsult], {
                                '{0}': `${formatInTimeZone(order.callTimeBegin, MOSCOW_TIMEZONE, 'dd.MM')}`,
                                '{1}': `${formatInTimeZone(order.callTimeBegin, MOSCOW_TIMEZONE, 'HH:mm')}`,
                                '{2}': `${formatInTimeZone(order.callTimeEnd, MOSCOW_TIMEZONE, 'HH:mm')}`,
                            })}
                        </Text>
                    ) : (
                        <Loading />
                    )}
                </Column>
            </ColumnsRow>
            <VSpacing base={6} />
            {order.meetingUrl && (
                <>
                    <ColumnsRow>
                        <Column xs="4" s="6" m="3" l="4">
                            <Text importance={TextImportance.Tertiary}>{trls[TrlKeys.meetingUrl]}</Text>
                        </Column>
                        <Column xs="4" s="6" m="6" l="7">
                            <Text>
                                <BlokoLink Element={SPALink} to={order.meetingUrl} target="_blank" disableVisited>
                                    {trls[TrlKeys.meetingLink]}
                                </BlokoLink>
                            </Text>
                        </Column>
                    </ColumnsRow>
                    <VSpacing base={6} />
                </>
            )}
            <ColumnsRow>
                <Column xs="4" s="6" m="3" l="4">
                    <Text importance={TextImportance.Tertiary}>{trls[TrlKeys.email]}</Text>
                </Column>
                <Column xs="4" s="6" m="6" l="7">
                    <Text>{order.email}</Text>
                </Column>
            </ColumnsRow>
            <VSpacing base={6} />
            <ColumnsRow>
                <Column xs="4" s="6" m="3" l="4">
                    <Text importance={TextImportance.Tertiary}>{trls[TrlKeys.phone]}</Text>
                </Column>
                <Column xs="4" s="6" m="6" l="7">
                    <Text Element="span">{order.phone}</Text>
                </Column>
            </ColumnsRow>
            <VSpacing base={6} />
            <ColumnsRow>
                <Column xs="4" s="6" m="3" l="4">
                    <Text importance={TextImportance.Tertiary}>{trls[TrlKeys.resumeAbout]}</Text>
                </Column>
                <Column xs="4" s="6" m="6" l="7">
                    {selectedResume && (
                        <Text>
                            <BlokoLink
                                Element={SPALink}
                                to={`/resume/${selectedResume.hash}`}
                                target="_blank"
                                disableVisited
                            >
                                {selectedResume.title}
                            </BlokoLink>
                        </Text>
                    )}
                </Column>
            </ColumnsRow>
            <VSpacing base={6} />
            <ColumnsRow>
                <Column xs="4" s="6" m="3" l="4">
                    <Text importance={TextImportance.Tertiary}>{trls[TrlKeys.consultationAbout]}</Text>
                </Column>
                <Column xs="4" s="6" m="6" l="7">
                    <Text>{order.description}</Text>
                </Column>
            </ColumnsRow>
            <VSpacing base={6} />
            <ColumnsRow>
                <Column xs="4" s="6" m="3" l="4"></Column>
                <Column xs="4" s="6" m="6" l="7">
                    <Button
                        onClick={() => {
                            push(pathname.replace('/order', '/order/edit'));
                        }}
                        data-qa="order-edit"
                    >
                        {trls[TrlKeys.actionChange]}
                    </Button>
                </Column>
            </ColumnsRow>
        </Column>
    );
};

export default translation(Awaiting);
